import {
  Actions,
  Attributes,
  AttributesRow,
  Card,
  Flag,
  openDialog,
  showNotification,
} from 'platform/components';
import {Box, Center, Heading, HStack, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';

import {
  EmailCredentialsOwnerType,
  OAuthEmailCredentialsResponseBody,
  useDeleteEmailCredentialsMutation,
} from '@dms/api/core';
import {useGetUserQuery} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {getUserName, handleApiError} from '@dms/shared';

import {buildArray, DOT_CHARACTER, parseDate} from 'shared/web';

import {getCredentialsProviderLabel} from '../utils/getCredentialsProviderLabel';
import {getCredentialsTypeLabel} from '../utils/getCredentialsTypeLabel';
import {getSmtpProviderLogo} from '../utils/getSmtpProviderLogo';
import {EmailOAuthServiceForm} from './EmailOAuthServiceForm';

interface OAuthCredentialsCardProps {
  credentials: OAuthEmailCredentialsResponseBody;
  ownerType: EmailCredentialsOwnerType;
}

export function OAuthCredentialsCard(props: OAuthCredentialsCardProps) {
  const formatDateTime = useDateTimeFormatter();

  const {data: updatedByUser} = useGetUserQuery(
    {id: props.credentials.updatedBy ?? ''},
    {skip: isNil(props.credentials.updatedBy)}
  );

  const {data: createdByUser} = useGetUserQuery(
    {id: props.credentials.createdBy ?? ''},
    {skip: isNil(props.credentials.createdBy)}
  );

  const [deleteCredentials, {isLoading: isDeletingCredentials}] =
    useDeleteEmailCredentialsMutation();

  const credentialLogo = getSmtpProviderLogo(props.credentials.provider);

  const handleUpdate = () =>
    openDialog(
      <EmailOAuthServiceForm
        defaultValues={{
          title: props.credentials.title,
          emailAddress: props.credentials.emailAddress,
        }}
        providerCode={props.credentials.provider}
        credentialsId={props.credentials.id}
        ownerType={props.ownerType}
      />,
      {
        title: `${i18n.t('entity.emailService.labels.editOAuthTitle')} ${props.credentials.title}`,
      }
    );

  const handleDelete = () =>
    deleteCredentials({id: props.credentials.id})
      .unwrap()
      .then(() => showNotification.success(i18n.t('general.notifications.successfullyDeleted')))
      .catch(handleApiError);

  const rows = buildArray<AttributesRow>()
    .add({
      label: i18n.t('entity.emailService.labels.emailAddress'),
      value: props.credentials.emailAddress,
    })
    .when(isNotNil(props.credentials.updatedAt), {
      label: i18n.t('general.labels.updatedAt'),
      value: props.credentials.updatedAt
        ? formatDateTime('dateTimeShort', parseDate(props.credentials.updatedAt))
        : null,
    })
    .when(isNotNil(props.credentials.updatedBy), {
      label: i18n.t('general.labels.updatedBy'),
      value: getUserName(updatedByUser),
    })
    .add({
      label: i18n.t('general.labels.createdAt'),
      value: formatDateTime('dateTimeShort', parseDate(props.credentials.createdAt)),
    })
    .add({
      label: i18n.t('general.labels.createdBy'),
      value: getUserName(createdByUser),
    });

  return (
    <Card key={props.credentials.id} variant="inlineGrey">
      <VStack spacing={4}>
        <HStack justify="space-between" align="center">
          <HStack spacing={2}>
            <Box height={12} width={12}>
              <Center height="100%" width="100%">
                {credentialLogo}
              </Center>
            </Box>
            <Center>
              <Heading size={4}>{`${DOT_CHARACTER} ${props.credentials.title}`}</Heading>
            </Center>
          </HStack>

          <Actions
            actions={[
              {
                type: 'button',
                title: i18n.t('general.actions.edit'),
                variant: 'ghostLink',
                leftIcon: 'image/edit',
                onClick: handleUpdate,
              },
              {
                type: 'button',
                title: i18n.t('general.actions.delete'),
                variant: 'dangerGhost',
                leftIcon: 'action/delete',
                onClick: handleDelete,
                isDisabled: isDeletingCredentials,
              },
            ]}
          />
        </HStack>

        <HStack spacing={2}>
          <Flag label={getCredentialsTypeLabel(props.credentials.type)} colorScheme="orange" />
          <Flag
            label={getCredentialsProviderLabel(props.credentials.provider)}
            colorScheme="orange"
            isSubtle
          />
        </HStack>

        <Attributes size="third" rows={rows} />
      </VStack>
    </Card>
  );
}
